// Connect embedded components (Stripe connect-js) for a club's connected
// account: onboarding, notification banner, account management, payouts —
// all rendered INSIDE Tap Leagues. Clubs never see dashboard.stripe.com
// (accounts are created with dashboard=none); Stripe still owns KYC,
// requirement collection and negative-balance liability.
//
// One StripeConnectInstance per connected account per session (Stripe's
// guidance: never one per component). fetchClientSecret always mints a fresh
// Account Session on the API — connect-js calls it on first mount and again
// whenever the session expires.
//
// Usage (see components/payments/StripeConnectElement.vue):
//   const {getConnect} = useStripeConnect();
//   const instance = getConnect(clubId, account.stripeAccountId);
//   const el = instance.create('account-onboarding');
//   el.setOnExit(() => ...); container.appendChild(el);
import {plainStripeError, sessionRefusalFrom} from '~/utils/stripeErrors.js';
import {reactive} from 'vue';
import {loadConnectAndInitialize} from '@stripe/connect-js';
import {useRuntimeConfig} from '#app';
import vuetifyColors from '~~/vuetify.colors';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {endpoints} from '~/network/endpoints.js';

// Keyed by clubId + Stripe account id. A connect-js instance holds a session
// for ONE connected account and only re-calls fetchClientSecret when that
// session expires — so after a disconnect / cancel / re-enable (new acct_) the
// old instance would keep showing the OLD account. Module-level so route
// changes reuse the instance; the Stripe user session survives navigations
// too. Never call logout() on unmount — only when the user logs out.
const instances = {};

// X238 (TEST_R99 M-2): the API's last refusal of a club's Account Session ({message, code}),
// keyed by clubId. Stripe's components only report "failed to authenticate", so
// StripeConnectElement shows this instead of its generic "could not load".
export const sessionRefusals = reactive({});

export function useStripeConnect() {
  const config = useRuntimeConfig();
  const {httpRequest} = useHttpRequest();

  const getConnect = (clubId, stripeAccountId) => {
    if (typeof window === 'undefined') return null;
    const key = `${clubId}:${stripeAccountId || ''}`;
    if (!instances[key]) {
      instances[key] = loadConnectAndInitialize({
        publishableKey: config.public.stripePublishableKey,
        fetchClientSecret: async () => {
          // X182: a failed session reaches Stripe's component as a plain sentence (it shows the thrown message).
          try {
            const res = await httpRequest(endpoints.payments.clubAccountSession(clubId));
            delete sessionRefusals[clubId];
            return res.clientSecret;
          } catch (e) {
            const refusal = sessionRefusalFrom(e);
            if (refusal) {
              sessionRefusals[clubId] = refusal;
              // A refused instance is not reused: once the admin fixes it, the next mount asks again.
              delete instances[key];
              throw new Error(refusal.message);
            }
            throw new Error(plainStripeError(e?.data ?? e?.response?._data ?? e?.message ?? e, 'Stripe could not load this section. Please try again'));
          }
        },
        locale: 'en-GB',
        // Poppins is served by nuxt-google-fonts; Stripe's iframes need their
        // own reference to the same stylesheet to render it.
        fonts: [{cssSrc: 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600&display=swap'}],
        appearance: {
          // Stripe's own overlays (e.g. bank-account edit) as dialogs so they
          // don't fight our v-dialog stack when a component sits in TlDialog.
          overlays: 'dialog',
          // Sized to match our Vuetify fields (outlined, density=compact →
          // ~40px tall, 16px text, 12px radius, hairline border) and buttons
          // (600 weight, no uppercase). Stripe's defaults are noticeably
          // smaller than our UI and too small for accessibility.
          variables: {
            fontFamily: 'Poppins, sans-serif',
            fontSizeBase: '16px',
            spacingUnit: '10px',
            colorPrimary: vuetifyColors.primary,
            colorText: vuetifyColors.text,
            colorSecondaryText: vuetifyColors.charcoal,
            colorBorder: 'rgba(26, 26, 26, 0.28)',
            colorDanger: vuetifyColors.error,
            colorBackground: vuetifyColors.white,
            offsetBackgroundColor: vuetifyColors.light,
            borderRadius: '12px',
            overlayBorderRadius: '16px',
            // form fields
            formBackgroundColor: vuetifyColors.white,
            formBorderRadius: '12px',
            formHighlightColorBorder: vuetifyColors.primary,
            formAccentColor: vuetifyColors.primary,
            formPlaceholderTextColor: vuetifyColors.textSubtle,
            inputFieldPaddingX: '14px',
            inputFieldPaddingY: '11px',
            bodyMdFontSize: '16px',
            bodySmFontSize: '14px',
            labelMdFontSize: '14px',
            labelMdFontWeight: '500',
            labelSmFontSize: '13px',
            labelSmFontWeight: '500',
            // buttons
            buttonBorderRadius: '12px',
            buttonPaddingX: '18px',
            buttonPaddingY: '10px',
            buttonLabelFontSize: '15px',
            buttonLabelFontWeight: '600',
            buttonLabelTextTransform: 'none',
            buttonPrimaryColorBackground: vuetifyColors.primary,
            buttonPrimaryColorBorder: vuetifyColors.primary,
            buttonPrimaryColorText: vuetifyColors.white,
            buttonSecondaryColorBackground: vuetifyColors.white,
            buttonSecondaryColorBorder: vuetifyColors.border,
            buttonSecondaryColorText: vuetifyColors.text,
            actionPrimaryColorText: vuetifyColors.primary,
            actionPrimaryTextDecorationLine: 'none',
            // badges (our tl-chip look) — Stripe caps badge radius at 24px; 999
            // triggers a validateAppearance warning and is clamped anyway.
            badgeBorderRadius: '24px',
            badgePaddingX: '10px',
            badgePaddingY: '3px',
            badgeLabelFontSize: '12px',
            badgeLabelFontWeight: '600',
            badgeSuccessColorBackground: vuetifyColors.successTint,
            badgeSuccessColorText: vuetifyColors.success,
            badgeSuccessColorBorder: vuetifyColors.successTint,
            badgeWarningColorBackground: vuetifyColors.warningTint,
            badgeWarningColorText: vuetifyColors.warning,
            badgeWarningColorBorder: vuetifyColors.warningTint,
            badgeDangerColorBackground: vuetifyColors.brandTint,
            badgeDangerColorText: vuetifyColors.primary,
            badgeDangerColorBorder: vuetifyColors.brandTint,
            // headings
            headingXlFontSize: '24px',
            headingXlFontWeight: '600',
            headingLgFontSize: '20px',
            headingLgFontWeight: '600',
            headingMdFontSize: '18px',
            headingMdFontWeight: '600',
            headingSmFontSize: '16px',
            headingSmFontWeight: '600',
            headingXsFontSize: '13px',
            headingXsFontWeight: '600',
          },
        },
      });
    }
    return instances[key];
  };

  /** Call when the Tap Leagues user logs out — invalidates every Stripe user session. */
  const logoutAll = () => {
    Object.values(instances).forEach((i) => {
      try { i.logout(); } catch { /* ignore */ }
    });
    Object.keys(instances).forEach((k) => delete instances[k]);
  };

  return {getConnect, logoutAll};
}
